import React from 'react';
import { useIntl } from 'react-intl';
import { Defect } from '../../../models/defects';
import { Flight } from '../../../models/flights';
import { DataValue } from '../../../utils/updateLocalDataObject';
import HistorySection from '../../HistoryTable/HistorySection';
import MxItemActions from '../../Actions/MxItemActions';
import { AircraftResource } from '../../../models/aircraft';
import useGetOperatorSetting from '../../../utils/useGetOperatorSetting';
import pluralizeOperatorTerminology from '../../../utils/pluralizeOperatorTerminology';
import ContentTabs from '../../ContentTabs/ContentTabs';
import ViewDefectDrawerHeader from './ViewDefectDrawerHeader';
import Card from './Card';
import ViewDefectDrawerDetails from './ViewDefectDrawerDetails';

export enum DefectDrawerTabKeys {
  OVERVIEW = 'overview',
  ACTIONS = 'actions',
  HISTORY = 'history',
}
interface ViewDefectDrawerProps {
  defectData: Defect;
  aircraftId: string;
  updateDefectData: (changes: { value: DataValue; key: string }[]) => void;
  flightsData: Flight[];
  isLoadingFlights: boolean;
  initialTabKey?: string;
  requiredSettings: {
    troubleShootingStepsRename: string;
    deferralApprovalReferenceRename: string;
    defectRename: string;
    reportedByTypeEnabled: boolean;
    stageOfFlightEnabled: boolean;
    stageOfFlightText: string;
    eropsRename: string;
    part145ApprovalNumberRename: string;
    engineerRename: string;
    engineerLicenceNumberRename: string;
    melNameOverride: string;
    cdlNameOverride: string;
    nefNameOverride: string;
    casNameOverride: string;
    otherNameOverride: string;
    showRichTextMel: boolean;
    dateFormat: string;
    editResolved: boolean;
    resolutionRename: string;
    currentLoginId: string;
    currentUserId: string;
    approvalReleaseMode: string;
    approvalReleaseName: string;
    isApprovalReleaseModeFeatureFlagEnabled: boolean;
    addActionsOnDefects: boolean;
  };
}

const ViewDefectDrawer: React.FC<ViewDefectDrawerProps> = ({
  defectData,
  aircraftId,
  updateDefectData,
  flightsData,
  isLoadingFlights,
  requiredSettings,
  initialTabKey,
}) => {
  const { formatMessage } = useIntl();
  const actionNameSingular =
    useGetOperatorSetting('action_name', aircraftId) ?? formatMessage({ id: 'text.action' });
  const actionNamePlural = pluralizeOperatorTerminology(actionNameSingular);

  return (
    <div>
      <ViewDefectDrawerHeader
        defectData={defectData}
        aircraftId={aircraftId}
        updateDefectData={updateDefectData}
        flightsData={flightsData}
        isLoadingFlights={isLoadingFlights}
      />
      <ContentTabs
        tabsTestId="ViewDefectDrawer-tabs"
        initialActiveKey={initialTabKey}
        tabs={[
          {
            key: DefectDrawerTabKeys.OVERVIEW,
            header: 'Overview',
            tabPanelTestId: 'ViewDefectDrawer-tab-overview',
            content: (
              <Card marginBottom={20} identifier="DefectDetailsWrapper">
                <ViewDefectDrawerDetails
                  defectData={defectData}
                  editResolved={requiredSettings.editResolved}
                  updateDefectData={updateDefectData}
                  requiredSettings={requiredSettings}
                  aircraftId={aircraftId}
                />
              </Card>
            ),
          },
          {
            key: DefectDrawerTabKeys.ACTIONS,
            header: actionNamePlural,
            tabPanelTestId: 'ViewDefectDrawer-tab-actions',
            content: defectData?.id ? (
              <MxItemActions
                mxItemId={defectData.id}
                aircraftId={aircraftId}
                dateFormat={requiredSettings.dateFormat}
                resource={AircraftResource.DEFECT}
                addActionsOnDefects={requiredSettings.addActionsOnDefects}
                isResolved={!!defectData?.resolved_at}
              />
            ) : null,
          },
          {
            key: DefectDrawerTabKeys.HISTORY,
            header: 'Audit Log',
            tabPanelTestId: 'ViewDefectDrawer-tab-history',
            content: <HistorySection title="defect" versions={defectData?.versions ?? []} />,
          },
        ]}
      />
    </div>
  );
};

export default ViewDefectDrawer;
